import { Injectable } from '@angular/core';

import { ApiService } from '@frontend/sports/common/api-utils';
import { BannersConfig } from '@frontend/sports/common/client-config-data-access';
import { CasinoGameViewModel, CasinoGameViewResponse } from '@frontend/sports/types/components/banners/models';
import { VymlGame, VymlGameResponse } from '@frontend/sports/types/components/banners/vyml';
import { BannersResponse, JackpotResponse } from '@frontend/sports/types/models/banners';
import { UserService } from '@frontend/sports/user/feature';
import { DeviceService, DslService, UserEvent } from '@frontend/vanilla/core';
import { isEmpty } from 'lodash-es';
import { Observable, catchError, filter, firstValueFrom, map, of, shareReplay, startWith, switchMap } from 'rxjs';

import { ChannelIds, KycStatus } from './banner.model';

@Injectable({ providedIn: 'root' })
export class BannersService {
    readonly data = this.userService.onAuthenticationChange$.pipe(
        startWith({} as UserEvent),
        filter(() => !isEmpty(this.bannerConfig.virtuals.enabledPages) || this.bannerConfig.enabled),
        switchMap(() => this.apiService.get<BannersResponse>('banners').pipe(catchError(() => of({ banners: [] })))),
        switchMap((response) => this.dslService.evaluateContent(response.banners)),
        shareReplay(1),
    );

    constructor(
        private apiService: ApiService,
        private bannerConfig: BannersConfig,
        private userService: UserService,
        private dslService: DslService,
        private deviceService: DeviceService,
    ) {}

    getChannelId(): ChannelIds {
        if (!this.deviceService.isMobile) {
            return ChannelIds.WC;
        }

        if (this.deviceService.isiOS) {
            return ChannelIds.IOS;
        }

        return ChannelIds.Android;
    }

    private getKycStatus(isKycVerified: boolean): KycStatus {
        if (!this.userService.isAuthenticated) {
            return KycStatus.Default;
        }
        if (isKycVerified) {
            return KycStatus.Verified;
        }
        return KycStatus.NonVerified;
    }

    getLobbyGames(isKycVerified: boolean = false): Promise<CasinoGameViewModel[]> {
        return firstValueFrom(
            this.apiService
                .get<CasinoGameViewResponse>('banners/getLobbyGames', {
                    channelId: this.getChannelId(),
                    kycStatus: this.getKycStatus(isKycVerified),
                })
                .pipe(map((r) => r.data)),
        );
    }

    getJackpotValues(jackpotGroups: string[]): Promise<JackpotResponse> {
        return firstValueFrom(this.apiService.get<JackpotResponse>('banners/getJackpotValues', { ids: jackpotGroups }));
    }

    getVirtualSportsforVyml(): Observable<VymlGame[]> {
        return this.apiService.get<VymlGameResponse>('banners/getVyml').pipe(map((r) => r.data));
    }
}
